/**
 * CostOverview - Recreation of Base44 CostOverview Component
 * Financial analytics and cost management dashboard
 * 
 * @description Professional cost overview with monthly spending, trends,
 * and budget tracking (simplified version for Phase 2)
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  AlertTriangle
} from 'lucide-react';

// TypeScript interfaces for type safety
interface CostData {
  current: number;
  previous: number;
  budget: number;
  currency: string;
}

interface CostOverviewProps {
  activeProvider: string;
  costData?: CostData;
}

export function CostOverview({ 
  activeProvider, 
  costData = {
    current: 1234.56,
    previous: 1156.78,
    budget: 1500.00,
    currency: 'USD'
  }
}: CostOverviewProps) {
  // Calculate cost metrics
  const costChange = costData.current - costData.previous;
  const costChangePercent = (costChange / costData.previous) * 100;
  const budgetUsed = (costData.current / costData.budget) * 100;
  const isIncreasing = costChange > 0;
  const isOverBudget = budgetUsed > 100;

  // Get provider-specific colors
  const getProviderColor = (): string => {
    switch (activeProvider) {
      case 'aws': return ' border-orange-200 text-orange-500';
      case 'gcp': return ' border-blue-200 text-blue-600';
      case 'azure': return ' border-cyan-200 text-cyan-500';
      default: return ' border-border  text-foreground';
    }
  };

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: costData.currency
    }).format(amount);
  };

  return (
    <Card className={`${getProviderColor()} !border-2 !bg-[var(--card)] shadow-[0px_0px_2px_0px_var(--card-foreground)]`}>
      <CardHeader>
        <div className="flex items-center justify-between ">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Cost Overview
          </CardTitle>
          <Badge variant="outline" className="text-xs">
            {activeProvider.toUpperCase()}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Current Month Spending */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">This Month</span>
            <div className="flex items-center gap-2">
              {isIncreasing ? (
                <TrendingUp className="w-4 h-4 text-red-500" />
              ) : (
                <TrendingDown className="w-4 h-4 text-green-500" />
              )}
              <span className={`text-xs font-medium ${
                isIncreasing ? 'text-red-600' : 'text-green-600'
              }`}>
                {isIncreasing ? '+' : ''}{costChangePercent.toFixed(1)}%
              </span>
            </div>
          </div>
          <div className="text-3xl font-bold">{formatCurrency(costData.current)}</div>
          <div className="text-sm text-muted-foreground">
            vs {formatCurrency(costData.previous)} last month
          </div>
        </div>

        {/* Budget Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Budget Usage</span>
            <div className="flex items-center gap-1">
              {isOverBudget && <AlertTriangle className="w-4 h-4 text-red-500" />}
              <span className={`text-sm font-medium ${
                isOverBudget ? 'text-red-600' : 'text-foreground'
              }`}>
                {budgetUsed.toFixed(0)}%
              </span>
            </div>
          </div>
          <Progress 
            value={Math.min(budgetUsed, 100)} 
            className="h-2"
          />
          <div className="text-sm text-muted-foreground">
            {formatCurrency(costData.budget)} monthly budget
          </div>
        </div>

        {/* Cost Breakdown by Service (Mock Data) */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium">Top Services</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Compute</span>
              <span className="font-medium">{formatCurrency(costData.current * 0.45)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Storage</span>
              <span className="font-medium">{formatCurrency(costData.current * 0.25)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Network</span>
              <span className="font-medium">{formatCurrency(costData.current * 0.20)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Other</span>
              <span className="font-medium">{formatCurrency(costData.current * 0.10)}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default CostOverview;